import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { waLink } from "@/lib/constants";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { User, Lock, Eye, EyeOff, LogIn, Loader2, MessageCircle, Users, FileText } from "lucide-react";
import { toast } from "sonner";

const BG =
  "https://customer-assets-v7afamib.emergentagent.net/job_parent-alert-system-9/artifacts/5utmiu86_%D8%B5%D9%88%D8%B1%D8%A9%20ChatGPT%2029%20%D8%B3%D8%A8%D8%AA%D9%85%D8%A8%D8%B1%202026%D8%8C%2006_22_12%20%D9%85.png";

const emptyTrial = { school_name: "", counselor_name: "", phone: "", students_count: "", city: "" };

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  const [trial, setTrial] = useState(emptyTrial);
  const [trialSending, setTrialSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(username, password);
      toast.success("تم تسجيل الدخول بنجاح");
      navigate(user.role === "SUPER_ADMIN" ? "/admin" : "/app");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitTrial = async () => {
    if (!trial.school_name.trim() || !trial.counselor_name.trim()) {
      toast.error("يرجى إدخال اسم المدرسة واسم المرشد");
      return;
    }
    if (trial.phone.length !== 9 || !trial.phone.startsWith("5")) {
      toast.error("رقم الجوال يجب أن يبدأ بـ 5 ويتكون من 9 أرقام");
      return;
    }
    setTrialSending(true);
    try {
      await api.post("/trial-requests", {
        school_name: trial.school_name,
        counselor_name: trial.counselor_name,
        phone: "+966" + trial.phone,
        students_count: parseInt(trial.students_count) || 0,
        city: trial.city,
      });
      toast.success("تم استلام طلبك بنجاح، سنتواصل معك قريباً");
      setTrialOpen(false);
      setTrial(emptyTrial);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setTrialSending(false);
    }
  };

  return (
    <div
      className="relative min-h-screen bg-cover bg-center"
      style={{ backgroundImage: `url(${BG})` }}
      dir="rtl"
    >
      <div className="absolute right-6 top-6 z-10 hidden lg:right-10 lg:top-8 lg:block">
        <Logo className="h-20 lg:h-28" />
      </div>

      <div className="flex min-h-screen items-center justify-center px-6 py-10 lg:px-20">
        <div className="flex w-full max-w-3xl flex-col items-stretch gap-6 lg:flex-row lg:items-center lg:gap-8">
          <div className="flex justify-center lg:hidden">
            <Logo className="h-20" />
          </div>
          <div className="w-full max-w-sm space-y-6 text-center lg:text-right">
            <div className="space-y-1">
              <h1 className="text-3xl font-extrabold leading-snug text-emerald-900 sm:text-4xl">
                تواصل أسهل
              </h1>
              <p className="text-xl font-bold text-teal-700">بين المدرسة وأولياء الأمور</p>
            </div>
            <div className="space-y-3">
              <Feature icon={MessageCircle} text="إرسال رسائل التنبيهات عبر واتساب بضغطة زر" />
              <Feature icon={Users} text="إدارة بيانات الطلاب وأولياء الأمور بسهولة" />
              <Feature icon={FileText} text="قوالب رسائل جاهزة ومخصصة" />
            </div>
            <p className="pt-2 text-sm font-semibold text-emerald-800/70">المدرسة دائماً معكم</p>
          </div>

          <div className="w-full max-w-sm space-y-6 rounded-3xl border border-white/60 bg-white/85 p-8 shadow-2xl shadow-emerald-900/10 backdrop-blur-xl">
            <div className="flex flex-col items-center gap-1">
              <h2 className="text-2xl font-extrabold text-emerald-900">مرحباً بك</h2>
              <p className="text-sm text-slate-400">سجّل الدخول إلى حسابك للمتابعة</p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="mb-1.5 block text-sm text-slate-600">اسم المستخدم</Label>
                <div className="relative">
                  <User className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input value={username} onChange={(e) => setUsername(e.target.value)} required
                    placeholder="اسم المستخدم" className="bg-white pr-10" data-testid="login-username" />
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block text-sm text-slate-600">كلمة المرور</Label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input type={show ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)} required placeholder="كلمة المرور"
                    className="bg-white px-10" data-testid="login-password" />
                  <button type="button" onClick={() => setShow(!show)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="remember" />
                <Label htmlFor="remember" className="text-sm text-slate-500">تذكرني</Label>
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit"
                className="h-12 w-full gap-2 rounded-xl bg-emerald-800 text-base font-bold hover:bg-emerald-900">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
                تسجيل الدخول
              </Button>
            </form>
            <div className="space-y-3 border-t border-slate-200/70 pt-4 text-center">
              <Dialog open={trialOpen} onOpenChange={setTrialOpen}>
                <DialogTrigger asChild>
                  <button data-testid="trial-request-btn"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-600 px-4 py-2 text-sm font-bold text-emerald-700 transition-colors hover:bg-emerald-50">
                    <MessageCircle className="h-4 w-4" /> طلب نسخة تجريبية
                  </button>
                </DialogTrigger>
                <DialogContent className="text-right" dir="rtl">
                  <DialogHeader><DialogTitle>طلب نسخة تجريبية</DialogTitle></DialogHeader>
                  <p className="text-sm text-slate-500">عبّئ البيانات التالية وسنتواصل معك لتفعيل التجربة.</p>
                  <div className="space-y-3">
                    <TrialField label="اسم المدرسة *" v={trial.school_name}
                      on={(v) => setTrial({ ...trial, school_name: v })} tid="trial-school" />
                    <TrialField label="اسم المرشد *" v={trial.counselor_name}
                      on={(v) => setTrial({ ...trial, counselor_name: v })} tid="trial-counselor" />
                    <div>
                      <Label className="mb-1 block text-xs text-slate-500">رقم الجوال *</Label>
                      <div className="flex items-center gap-2">
                        <span dir="ltr" className="rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm text-slate-600">+966</span>
                        <Input value={trial.phone} dir="ltr"
                          onChange={(e) => setTrial({ ...trial, phone: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                          placeholder="5XXXXXXXX" className="text-right font-mono" data-testid="trial-phone" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="mb-1 block text-xs text-slate-500">عدد الطلاب</Label>
                        <Input type="number" value={trial.students_count}
                          onChange={(e) => setTrial({ ...trial, students_count: e.target.value })}
                          placeholder="مثال: 400" data-testid="trial-students" />
                      </div>
                      <TrialField label="المدينة" v={trial.city}
                        on={(v) => setTrial({ ...trial, city: v })} tid="trial-city" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={submitTrial} disabled={trialSending}
                      className="w-full gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="trial-submit">
                      {trialSending ? <Loader2 className="h-4 w-4 animate-spin" /> : "إرسال الطلب"}
                    </Button>
                  </DialogFooter>
                  <a href={waLink("السلام عليكم، أرغب بطلب نسخة تجريبية من منصة معاكم")}
                    target="_blank" rel="noopener noreferrer"
                    className="block text-center text-xs text-emerald-700 underline">
                    أو تواصل معنا مباشرة عبر واتساب
                  </a>
                </DialogContent>
              </Dialog>
              <p className="text-[11px] leading-relaxed text-slate-400">
                إعداد شركة شفرة المصدر للاتصالات وتقنية المعلومات
                <br />سجل تجاري 7034897616
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Feature = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-3">
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
      <Icon className="h-5 w-5" />
    </div>
    <span className="text-base font-medium text-slate-700">{text}</span>
  </div>
);

const TrialField = ({ label, v, on, tid }) => (
  <div>
    <Label className="mb-1 block text-xs text-slate-500">{label}</Label>
    <Input value={v} onChange={(e) => on(e.target.value)} data-testid={tid} />
  </div>
);
